// ===============================================
// 📝 CREATE TICKET FORM - COMPONENTE REACT
// ===============================================
// Archivo: src/components/CreateTicketForm.tsx

import React, { useState, useRef } from 'react';
import { 
  X, 
  Upload, 
  AlertCircle, 
  CheckCircle, 
  Paperclip,
  Trash2,
  FileText,
  Image,
  Video
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { ticketService, CreateTicketData, fileUtils } from '../services/ticketService';

// ===============================================
// 🔧 INTERFACES
// ===============================================

interface CreateTicketFormProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated: () => void;
  initialData?: Partial<CreateTicketData>;
}

interface FormErrors {
  title?: string;
  description?: string;
  motor?: string;
  configuration?: string;
  component?: string;
  user_name?: string;
  user_email?: string;
  attachments?: string;
}

// ===============================================
// 📝 COMPONENTE PRINCIPAL
// ===============================================

const CreateTicketForm: React.FC<CreateTicketFormProps> = ({
  isOpen,
  onClose,
  onTicketCreated,
  initialData = {}
}) => {
  const { isDark } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ===============================================
  // 🎯 ESTADOS
  // ===============================================

  const [formData, setFormData] = useState<CreateTicketData>({
    title: initialData.title || '',
    description: initialData.description || '',
    priority: initialData.priority || 'medium',
    category: initialData.category || 'diagnostic',
    motor: initialData.motor || '',
    configuration: initialData.configuration || '',
    component: initialData.component || '',
    part_number: initialData.part_number || '',
    user_name: initialData.user_name || '',
    user_email: initialData.user_email || '',
    estimated_hours: initialData.estimated_hours || undefined,
    attachments: []
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);

  // ===============================================
  // 🎨 ESTILOS
  // ===============================================

  const modalClasses = `fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`;
  const formClasses = `w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
    isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
  }`;
  const inputClasses = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark 
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
      : 'bg-white border-gray-300 text-gray-800 placeholder-gray-500'
  }`;
  const selectClasses = inputClasses;
  const buttonClasses = `px-4 py-2 rounded-md font-medium transition-colors duration-200`;

  // ===============================================
  // 🔧 FUNCIONES AUXILIARES
  // ===============================================

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validaciones requeridas
    if (!formData.title.trim()) {
      newErrors.title = 'El título es requerido';
    } else if (formData.title.length > 300) {
      newErrors.title = 'El título no puede exceder 300 caracteres';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    } else if (formData.description.length > 800) {
      newErrors.description = 'La descripción no puede exceder 800 caracteres';
    }

    if (!formData.motor.trim()) {
      newErrors.motor = 'El motor es requerido';
    }

    if (!formData.configuration.trim()) {
      newErrors.configuration = 'La configuración es requerida';
    }

    if (!formData.component.trim()) {
      newErrors.component = 'El componente es requerido';
    }

    if (!formData.user_name.trim()) {
      newErrors.user_name = 'El nombre de usuario es requerido';
    }

    if (!formData.user_email.trim()) {
      newErrors.user_email = 'El email es requerido';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.user_email)) {
        newErrors.user_email = 'El email no tiene un formato válido';
      }
    }

    // Validar archivos adjuntos
    if (formData.attachments && formData.attachments.length > 5) {
      newErrors.attachments = 'Máximo 5 archivos permitidos';
    }

    for (const file of formData.attachments || []) {
      if (!fileUtils.isValidFileType(file)) {
        newErrors.attachments = 'Tipo de archivo no permitido';
        break;
      }
      if (!fileUtils.isValidFileSize(file)) {
        newErrors.attachments = 'Archivo muy grande (máximo 10MB)';
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof CreateTicketData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo modificado
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Contar caracteres para descripción
    if (field === 'description') {
      setCharacterCount(value.length);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;

    // Validar archivos antes de agregar
    const validFiles = files.filter(file => {
      if (!fileUtils.isValidFileType(file)) {
        alert(`Archivo ${file.name}: Tipo no permitido`);
        return false;
      }
      if (!fileUtils.isValidFileSize(file)) {
        alert(`Archivo ${file.name}: Muy grande (máximo 10MB)`);
        return false;
      }
      return true;
    });

    const currentFiles = formData.attachments || [];
    const newFiles = [...currentFiles, ...validFiles];

    if (newFiles.length > 5) {
      alert('Máximo 5 archivos permitidos');
      return;
    }

    handleInputChange('attachments', newFiles);
    
    // Limpiar input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    const newFiles = (formData.attachments || []).filter((_, i) => i !== index);
    handleInputChange('attachments', newFiles);
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (file.type.startsWith('video/')) return <Video className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  // ===============================================
  // 📤 ENVÍO DEL FORMULARIO
  // ===============================================

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!validateForm()) return;
  
  setIsSubmitting(true);
  
  try {
    // ✅ PREPARAR DATOS COMPLETOS
    const submitData: CreateTicketData = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      priority: formData.priority || 'medium',
      category: formData.category || 'diagnostic',
      motor: formData.motor.trim(),
      configuration: formData.configuration.trim(),
      component: formData.component.trim(),
      part_number: formData.part_number?.trim() || '',
      user_name: formData.user_name.trim(),
      user_email: formData.user_email.trim(),
      estimated_hours: formData.estimated_hours || 0,
      attachments: (formData.attachments || []).filter(file => 
        file && file instanceof File && file.size > 0
      )
    };

    // ✅ DEBUG ANTES DEL ENVÍO
    console.log('🚀 ENVIANDO DATOS DEL TICKET:', submitData);
    console.log('🚀 VERIFICAR CAMPOS OBLIGATORIOS:');
    console.log('  ✓ title:', !!submitData.title);
    console.log('  ✓ description:', !!submitData.description);
    console.log('  ✓ motor:', !!submitData.motor);
    console.log('  ✓ configuration:', !!submitData.configuration);
    console.log('  ✓ component:', !!submitData.component);
    console.log('  ✓ user_name:', !!submitData.user_name);
    console.log('  ✓ user_email:', !!submitData.user_email);

    const createdTicket = await ticketService.createTicket(submitData);
    
    console.log('✅ Ticket creado exitosamente:', createdTicket);
    
    // Llamar callback de éxito
    if (onTicketCreated) {
      onTicketCreated(createdTicket);
    }
    
    // Cerrar modal
    onClose();
    
  } catch (error) {
    console.error('❌ Error en handleSubmit:', error);
    setErrors({ general: error.message || 'Error creando ticket' });
  } finally {
    setIsSubmitting(false);
  }
};

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      category: 'diagnostic',
      motor: '',
      configuration: '',
      component: '',
      part_number: '',
      user_name: '',
      user_email: '',
      estimated_hours: undefined,
      attachments: []
    });
    setErrors({});
    setCharacterCount(0);
    setSubmitSuccess(false);
  };

  // ===============================================
  // 🎨 RENDERIZADO
  // ===============================================

  if (!isOpen) return null;

  if (submitSuccess) {
    return (
      <div className={modalClasses}>
        <div className={`${formClasses} p-8 text-center`}>
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">¡Ticket Creado Exitosamente!</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Su ticket ha sido creado y los técnicos serán notificados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={modalClasses}>
      <div className={formClasses}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold">Crear Nuevo Ticket</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          
          {/* Información Básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Título */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Título del Ticket *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`${inputClasses} ${errors.title ? 'border-red-500' : ''}`}
                placeholder="Describe brevemente el problema"
                maxLength={300}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* Descripción */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Descripción Detallada *
                <span className={`ml-2 text-xs ${characterCount > 800 ? 'text-red-500' : 'text-gray-500'}`}>
                  {characterCount}/800
                </span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className={`${inputClasses} ${errors.description ? 'border-red-500' : ''}`}
                rows={4}
                placeholder="Describe el problema en detalle, incluyendo síntomas y condiciones"
                maxLength={800}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.description}
                </p>
              )}
            </div>

            {/* Prioridad */}
            <div>
              <label className="block text-sm font-medium mb-2">Prioridad</label>
              <select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className={selectClasses}
              >
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="critical">Crítica</option>
              </select>
            </div>

            {/* Categoría */}
            <div>
              <label className="block text-sm font-medium mb-2">Categoría</label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className={selectClasses}
              >
                <option value="diagnostic">Diagnóstico</option>
                <option value="maintenance">Mantenimiento</option>
                <option value="repair">Reparación</option>
                <option value="parts">Partes/Piezas</option>
                <option value="documentation">Documentación</option>
              </select>
            </div>

          </div>

          {/* Información Técnica */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Información Técnica</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              {/* Motor */}
              <div>
                <label className="block text-sm font-medium mb-2">Motor *</label>
                <input
                  type="text"
                  value={formData.motor}
                  onChange={(e) => handleInputChange('motor', e.target.value)}
                  className={`${inputClasses} ${errors.motor ? 'border-red-500' : ''}`}
                  placeholder="C27, C175, etc."
                />
                {errors.motor && (
                  <p className="text-red-500 text-sm mt-1">{errors.motor}</p>
                )}
              </div>

              {/* Configuración */}
              <div>
                <label className="block text-sm font-medium mb-2">Configuración *</label>
                <input
                  type="text"
                  value={formData.configuration}
                  onChange={(e) => handleInputChange('configuration', e.target.value)}
                  className={`${inputClasses} ${errors.configuration ? 'border-red-500' : ''}`}
                  placeholder="L4G, AJ8, etc."
                />
                {errors.configuration && (
                  <p className="text-red-500 text-sm mt-1">{errors.configuration}</p>
                )}
              </div>

              {/* Componente */}
              <div>
                <label className="block text-sm font-medium mb-2">Componente *</label>
                <input
                  type="text"
                  value={formData.component}
                  onChange={(e) => handleInputChange('component', e.target.value)}
                  className={`${inputClasses} ${errors.component ? 'border-red-500' : ''}`}
                  placeholder="válvulas, filtros, etc."
                />
                {errors.component && (
                  <p className="text-red-500 text-sm mt-1">{errors.component}</p>
                )}
              </div>

            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              
              {/* Número de Pieza */}
              <div>
                <label className="block text-sm font-medium mb-2">Número de Pieza</label>
                <input
                  type="text"
                  value={formData.part_number}
                  onChange={(e) => handleInputChange('part_number', e.target.value)}
                  className={inputClasses}
                  placeholder="CAT-V27-001 (opcional)"
                />
              </div>

              {/* Horas Estimadas */}
              <div>
                <label className="block text-sm font-medium mb-2">Horas Estimadas</label>
                <input
                  type="number"
                  value={formData.estimated_hours || ''}
                  onChange={(e) => handleInputChange('estimated_hours', e.target.value ? parseInt(e.target.value) : undefined)}
                  className={inputClasses}
                  placeholder="Tiempo estimado (opcional)"
                  min="1"
                  max="999"
                />
              </div>

            </div>
          </div>

          {/* Información del Usuario */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Información de Contacto</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium mb-2">Nombre Completo *</label>
                <input
                  type="text"
                  value={formData.user_name}
                  onChange={(e) => handleInputChange('user_name', e.target.value)}
                  className={`${inputClasses} ${errors.user_name ? 'border-red-500' : ''}`}
                  placeholder="Tu nombre completo"
                />
                {errors.user_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.user_name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.user_email}
                  onChange={(e) => handleInputChange('user_email', e.target.value)}
                  className={`${inputClasses} ${errors.user_email ? 'border-red-500' : ''}`}
                  placeholder="tu@empresa.com"
                />
                {errors.user_email && (
                  <p className="text-red-500 text-sm mt-1">{errors.user_email}</p>
                )}
              </div>

            </div>
          </div>

          {/* Archivos Adjuntos */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Archivos Adjuntos</h3>
            
            <div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4"
              />
              
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`${buttonClasses} border-2 border-dashed ${
                  isDark 
                    ? 'border-gray-600 hover:border-gray-500 text-gray-400 hover:text-gray-300' 
                    : 'border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-700'
                } w-full p-4`}
                disabled={isSubmitting}
              >
                <Upload className="w-5 h-5 mx-auto mb-2" />
                <span className="text-sm">
                  Haz clic para subir archivos (máximo 5, 10MB cada uno)
                </span>
                <span className="text-xs block mt-1">
                  PDF, DOC, DOCX, JPG, PNG, GIF, MP4
                </span>
              </button>

              {errors.attachments && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.attachments}
                </p>
              )}

              {/* Lista de archivos */}
              {formData.attachments && formData.attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  {formData.attachments.map((file, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 border rounded-md ${
                        isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        {getFileIcon(file)}
                        <div>
                          <p className="text-sm font-medium">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {fileUtils.formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="p-1 text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className={`${buttonClasses} ${
                isDark 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            
            <button
              type="submit"
              className={`${buttonClasses} bg-blue-600 hover:bg-blue-700 text-white ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creando...' : 'Crear Ticket'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};
export default CreateTicketForm;
// Al final de ticketService.ts

export const fileUtils = {
  // Validar tipo de archivo
  validateFileType(file: File, allowedTypes: string[]): boolean {
    return allowedTypes.some(type => file.type.includes(type));
  },

  // Validar tamaño de archivo (en MB)
  validateFileSize(file: File, maxSizeMB: number): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  },

  // Formatear tamaño de archivo
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  },

  // Convertir archivo a base64
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }
};